// src/components/SuggestionBox.jsx
//
// A small, always-available way for users AND creators to tell
// Viktor what they actually want. Mounted globally in App.js (same
// tier as InstallAppPrompt/PipEgg), reachable from every signed-in
// page.
//
// Flow: role question first ("are you a creator or a fan?"), THEN
// the text box — asked every time, not inferred from the account's
// isCreator flag, since this is meant to feel like a small warm
// ritual, not a form that silently guesses at you.
//
// Writes to a `suggestions` collection:
//   { uid, displayName, role: "creator" | "fan", text, createdAt, status: "new" }
//
// Read on the admin side via AdminSuggestions (src/pages/admin/) —
// see SUGGESTION-BOX-WIRING.md for the route + Firestore rule.
//
// Reuses the existing .modal-overlay / .modal-card / .modal-close
// classes already established in App.js's BecomeCreatorModal.

import { useState } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import "./SuggestionBox.css";

const MAX_LENGTH = 500;

function IconStar(props) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" stroke="none" {...props}>
      <path d="M12 2l1.6 5.8L19 9l-5.4 1.2L12 16l-1.6-5.8L5 9l5.4-1.2z" />
    </svg>
  );
}
function IconMessage(props) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M8 12h.01M12 12h.01M16 12h.01" />
      <path d="M21 12c0 4.4-4 8-9 8a10 10 0 0 1-3-.4L3 21l1.4-4A7.9 7.9 0 0 1 3 12c0-4.4 4-8 9-8s9 3.6 9 8z" />
    </svg>
  );
}
function IconHeart(props) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
    </svg>
  );
}

export default function SuggestionBox() {
  const { currentUser, userProfile } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("role"); // "role" | "form" | "success"
  const [role, setRole] = useState(null); // "creator" | "fan"
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const openBox = () => {
    setStep("role");
    setRole(null);
    setText("");
    setError(null);
    setOpen(true);
  };

  const closeAndReset = () => {
    setOpen(false);
    // Reset after the close animation would run, not instantly, so a
    // reopen mid-fade doesn't flash back to an empty/prior state.
    setTimeout(() => {
      setStep("role");
      setRole(null);
      setText("");
      setError(null);
    }, 200);
  };

  const chooseRole = (chosen) => {
    setRole(chosen);
    setStep("form");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser || !text.trim() || !role) return;
    setSubmitting(true);
    setError(null);
    try {
      await addDoc(collection(db, "suggestions"), {
        uid: currentUser.uid,
        displayName: userProfile?.displayName || currentUser.displayName || "Someone",
        role,
        text: text.trim().slice(0, MAX_LENGTH),
        createdAt: serverTimestamp(),
        status: "new",
      });
      setStep("success");
    } catch (err) {
      console.error("SuggestionBox submit error:", err);
      setError("Something went wrong — try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentUser) return null;

  return (
    <>
      <button
        type="button"
        className="sugg-fab"
        onClick={openBox}
        aria-label="Suggest something"
      >
        <IconMessage />
        Suggest something
      </button>

      {open && (
        <div className="modal-overlay" onClick={closeAndReset}>
          <div className="modal-card sugg-card" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeAndReset} aria-label="Close">×</button>

            {step === "role" && (
              <div className="sugg-role-step">
                <div className="sugg-header">
                  <h2 className="sugg-title">Thanks for taking the time.</h2>
                  <p className="sugg-sub">We love hearing suggestions. But first — are you a creator or a fan?</p>
                </div>
                <div className="sugg-role-choices">
                  <button type="button" className="sugg-role-btn" onClick={() => chooseRole("creator")}>
                    <IconStar className="sugg-role-icon" />
                    <span>I'm a creator</span>
                  </button>
                  <button type="button" className="sugg-role-btn" onClick={() => chooseRole("fan")}>
                    <IconHeart className="sugg-role-icon" />
                    <span>I'm a fan</span>
                  </button>
                </div>
              </div>
            )}

            {step === "form" && (
              <>
                <div className="sugg-header">
                  <h2 className="sugg-title">What's on your mind?</h2>
                  <p className="sugg-sub">
                    Tell us straight — a feature, a fix, a "why doesn't this exist yet."
                    Viktor reads every one of these himself.
                  </p>
                  <button type="button" className="sugg-role-change" onClick={() => setStep("role")}>
                    Answering as a {role} · change
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  <textarea
                    className="sugg-textarea"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="I wish 100VO had..."
                    maxLength={MAX_LENGTH}
                    rows={5}
                    autoFocus
                  />
                  <div className="sugg-charcount">{text.length}/{MAX_LENGTH}</div>

                  {error && <p className="sugg-error">{error}</p>}

                  <button
                    type="submit"
                    className="sugg-submit"
                    disabled={!text.trim() || submitting}
                  >
                    {submitting ? "Sending…" : "Send it"}
                  </button>
                </form>
              </>
            )}

            {step === "success" && (
              <div className="sugg-success">
                <div className="sugg-success-icon">✓</div>
                <h2 className="sugg-title">Got it — thank you.</h2>
                <p className="sugg-sub">This goes straight to Viktor, not a support queue.</p>
                <button type="button" className="sugg-submit" onClick={closeAndReset}>Done</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}